<script lang="ts">
  import ConfirmAccountDetailsModal from "@retailer/svelte/Cart/Page/DistributorSection/ConfirmAccountDetails/ConfirmAccountDetailsModal.svelte";
  import type {
    RetailerDistributor,
    RetailerDistributorDistributorAccount,
  } from "@shared/types/RetailerDistributor";
  import AddRepModalService from "@shared/svelte/Modules/AddRepModalService";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import {
    CartOrderAlertEntity,
    CartOrderEntity,
    StatusEntity,
  } from "@shared/services/event_tracking/entities";
  import Flash from "@shared/services/flashv2.js";

  export let salesRepNames: string[];
  export let requiresMatchConfirmation: boolean;
  export let highlightErrors: boolean;
  export let distributorId: number;
  export let orderId: number;
  export let reloadCart: () => {};

  let shouldShowConfirmAccountDetailsModal = false;
  let retailerDistributor: RetailerDistributor;
  let distributorAccount: RetailerDistributorDistributorAccount;
  let accountNumber: string;
  let salesRepNamesForDisplay: string[] = salesRepNames;

  const addRepModalService = new AddRepModalService();
  let accountNumberChanged = false;
  let updatedSalesReps = [];

  const cartOrderEntity = new CartOrderEntity({ distributorId, orderId });
  const { trackMrcEvents, trackClick } = EventTracking;

  const onShowConfirmAccountDetails = (): void => {
    trackClick("cart_confirm_account_details_open_modal", [cartOrderEntity]);

    HttpPromise({
      url: "/api/retailer/retailer_distributors",
      method: "GET",
      data: {
        distributor_id: distributorId,
        limit: 1,
      },
    }).then(retailerDistributors => {
      retailerDistributor = retailerDistributors[0];
      distributorAccount = retailerDistributor.distributor_account;
      accountNumber = retailerDistributor.account_number;
      salesRepNamesForDisplay = salesRepNames;

      shouldShowConfirmAccountDetailsModal = true;
    });
  };

  const hideConfirmAccountDetailsModal = () => {
    shouldShowConfirmAccountDetailsModal = false;
    trackClick("cart_confirm_account_details_hide_modal", [cartOrderEntity]);
  };

  const confirmAutomatch = () => {
    HttpPromise({
      url: `/api/retailer/retailer_distributors/${retailerDistributor.id}`,
      method: "PUT",
      data: {
        retailer_distributor: {
          requires_match_confirmation: false,
        },
      },
    }).then(() => {
      reloadCart();
      Flash.now({
        type: "success",
        message: "Account information verified",
      });
    });
  };

  const onConfirmAccountDetails = async () => {
    if (accountNumberChanged) {
      trackClick("cart_confirm_account_details", [
        cartOrderEntity,
        new StatusEntity({ status: "acct_number_changed" }),
      ]);
      await addRepModalService.connectAccount(
        retailerDistributor.distributor,
        distributorAccount.account_number,
        updatedSalesReps.map(rep => rep.id),
      );
    } else {
      trackClick("cart_confirm_account_details", [
        cartOrderEntity,
        new StatusEntity({ status: "acct_number_not_changed" }),
      ]);
    }

    confirmAutomatch();
  };

  const onChangeAccountNumber = async (newAccountNumber: string): Promise<{ error?: string }> => {
    if (newAccountNumber != accountNumber) {
      const getAccountResult = await HttpPromise({
        method: "GET",
        url: `/api/retailer/distributors/${distributorId}/distributor_accounts/${newAccountNumber}`,
      });

      if (getAccountResult.error || !getAccountResult.account) {
        trackClick("cart_confirm_save_new_account", [
          cartOrderEntity,
          new StatusEntity({ status: "acct_number_invalid" }),
        ]);

        return { error: "Account number is invalid" };
      }

      accountNumber = newAccountNumber;
      accountNumberChanged = true;
      distributorAccount = getAccountResult.account;
      updatedSalesReps = getAccountResult.sales_reps;
      salesRepNamesForDisplay = updatedSalesReps.map(rep => rep.name);
      trackClick("cart_confirm_save_new_account", [
        cartOrderEntity,
        new StatusEntity({ status: "acct_number_valid" }),
      ]);
    }

    return { error: null };
  };

  const handleEditEvent = () => {
    trackClick("cart_confirm_account_details_edit", [cartOrderEntity]);
  };
</script>

{#if requiresMatchConfirmation}
  <div
    style="flex-wrap: nowrap;"
    class="fizz-stack-12 fizz-alert--small fizz-flex-row"
    class:fizz-alert-warning={!highlightErrors}
    class:fizz-alert-error={highlightErrors}
    data-testid="confirm-account-details-alert"
    use:trackMrcEvents={[
      [
        new CartOrderEntity({ distributorId: distributorId, orderId: orderId }),
        new CartOrderAlertEntity({ alert_type: "confirm_account_details" }),
      ],
    ]}
  >
    <p class="fizz-text-small">
      <span class="fizz-text-strong">Account confirmation required:</span>
      Please review and confirm your account details before sending this order.
    </p>
    <div>
      <button
        class="fizz-button fizz-button-small fizz-max-content"
        on:click={onShowConfirmAccountDetails}
      >
        Confirm account details
      </button>
    </div>
  </div>

  {#if shouldShowConfirmAccountDetailsModal}
    <ConfirmAccountDetailsModal
      salesRepNames={salesRepNamesForDisplay}
      {accountNumber}
      {distributorAccount}
      distributorName={retailerDistributor.distributor.name}
      onClose={hideConfirmAccountDetailsModal}
      onConfirm={onConfirmAccountDetails}
      {onChangeAccountNumber}
      {handleEditEvent}
    />
  {/if}
{/if}
